import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import {
  isNotificationTypeEnabled,
  normalizeNotificationPreferences,
} from "@/lib/notificationPreferences";
import { showBrowserNotification } from "@/lib/browserNotifications";

type NotificationRecord = {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: string | null;
  link_to: string | null;
  created_at: string;
  read: boolean | null;
  read_at?: string | null;
};

function normalizeNotification(notification: NotificationRecord) {
  return {
    ...notification,
    isRead: Boolean(notification.read_at || notification.read),
  };
}

export function useNotifications() {
  const { user, profile } = useAuth();
  const { entitlements } = useSubscription();
  const queryClient = useQueryClient();
  const [notifications, setNotifications] = useState<(NotificationRecord & { isRead: boolean })[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const notificationPreferences = useMemo(
    () => normalizeNotificationPreferences(profile?.notification_preferences),
    [profile?.notification_preferences],
  );
  const browserPushEnabled = Boolean(profile?.browser_push_enabled);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setIsLoading(false);
      return;
    }

    let mounted = true;

    const loadNotifications = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (!mounted) return;

      if (error) {
        toast.error("Could not load notifications", {
          description: error.message,
        });
        setNotifications([]);
      } else {
        setNotifications(
          (data || [])
            .filter((notification) => isNotificationTypeEnabled(notification.type, notificationPreferences))
            .map(normalizeNotification),
        );
      }

      setIsLoading(false);
    };

    loadNotifications();

    if (!entitlements.realtimeNotifications) {
      return () => {
        mounted = false;
      };
    }

    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const notification = normalizeNotification(payload.new as NotificationRecord);
          if (!isNotificationTypeEnabled(notification.type, notificationPreferences)) {
            return;
          }
          setNotifications((current) => [notification, ...current]);
          toast(notification.title, {
            description: notification.message,
          });
          void (async () => {
            if (!browserPushEnabled) {
              return;
            }

            await showBrowserNotification(notification.title, {
              body: notification.message,
              tag: `notification-${notification.id}`,
              url: notification.link_to || "/notifications",
            });
          })();
          queryClient.invalidateQueries({ queryKey: ["notifications", user.id] });
        },
      )
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, [
    browserPushEnabled,
    entitlements.realtimeNotifications,
    profile?.browser_push_enabled,
    queryClient,
    user,
  ]);

  const markAsRead = async (id: string) => {
    const readAt = new Date().toISOString();
    const { error } = await supabase
      .from("notifications")
      .update({ read_at: readAt, read: true } as never)
      .eq("id", id);

    if (error) {
      throw error;
    }

    setNotifications((current) =>
      current.map((notification) =>
        notification.id === id
          ? { ...notification, read: true, read_at: readAt, isRead: true }
          : notification,
      ),
    );
  };

  const markAllAsRead = async () => {
    if (!user) return;

    const readAt = new Date().toISOString();
    const { error } = await supabase
      .from("notifications")
      .update({ read_at: readAt, read: true } as never)
      .eq("user_id", user.id)
      .is("read_at", null);

    if (error) {
      throw error;
    }

    setNotifications((current) =>
      current.map((notification) => ({
        ...notification,
        read: true,
        read_at: readAt,
        isRead: true,
      })),
    );
  };

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.isRead).length,
    [notifications],
  );

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    isLoading,
    realtimeEnabled: entitlements.realtimeNotifications,
  };
}
